import React, { useState, useEffect, useCallback } from "react";
import DataFetchHookDomainMonitor from "../../helper/DataFetchHookDomainMonitor";
import { API_BASE_URL } from "../../config/config";
import { Table, PageHeader, Card, Form, Select, Row, Col, Button, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import ExportModalV3 from "../../helper/ExportModalV3";
import CustomPaginationV2 from "../../helper/Table/CustomPaginationV2";
import CustomDateRangeV3 from "../../helper/Table/CustomDateRangeV3";
import moment from "moment";
import axios from "axios";
import Cookies from "js-cookie";
import { API_DOMAIN_MONITOR } from "../../config/config";
import createExportDateHandler from "../../helper/createExportDateHandler";

const { Option } = Select;

const DATA_URL = "/monitoring-alerts";
const DATA_URL_EXPORT = API_BASE_URL + "/v3/domainmonitor/alerts/reports";
const DOMAINS_LIST_URL = API_DOMAIN_MONITOR + "/v1/domains";

const MAX_DATERANGE = 3;
const DEFAULT_DATE_RANGE = 7;
const DEFAULT_DATE_PERIOD = "days";
const START_DATE = moment().subtract(DEFAULT_DATE_RANGE, DEFAULT_DATE_PERIOD);
const END_DATE = moment();

const MonitoringAlerts = (props) => {
    const [{ data, totalResult, isLoading, requestData }, , setRequestData] = DataFetchHookDomainMonitor(
        DATA_URL,
        "GET",
        {
            startDate: START_DATE,
            endDate: END_DATE,
            page: 1,
            size: 20,
            order: "DESC",
            sort_field: "timestamp"
        }
    );

    const [dates, setDates] = useState([START_DATE, END_DATE]);
    const [currentPage, setCurrentPage] = useState(1);
    const { t = (key) => key } = props;
    const [isDateExceeded, setIsDateExceeded] = useState(false);
    const [domainList, setDomainList] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalContent] = useState({ title: '', content: null });

    const [form] = Form.useForm();

    const formValInit = useCallback(() => {
        form.setFieldsValue({
        dateRange: [START_DATE, END_DATE],
        domainId: null,
        order: "DESC",
        sort_field: "timestamp",
    });
    }, [form]);

    const getDomainList = useCallback(() => {
        axios
            .get(
                `${DOMAINS_LIST_URL}?limit=1000`,
                {
                    headers: {
                        Authorization: "Bearer " + Cookies.get("dm_token"),
                    }
                }
            )
            .then(({ data }) => {
                setDomainList((data && data.data) || []);
            })
            .catch(() => false);
    }, []);

    useEffect(() => {
        formValInit();
        getDomainList();
    }, [formValInit, getDomainList]);


    const handleChange = (pagination, filters, sorter, { currentDataSource }) => {
        var orderBy = 'ASC';
        if (sorter.order === 'descend') {
            orderBy = 'DESC';
        }
        var sortField = 'timestamp';
        if (sorter.field !== undefined) {
            sortField = sorter.field;
        }
        var p = pagination.current;
        if (Object.keys(sorter).length > 0 && requestData.sort_field !== sortField) {
            p = 1;
            setCurrentPage(1);
        }

        setRequestData({ ...requestData, order: orderBy, sort_field: sortField, page: p, size: pagination.pageSize });
    };

    const onFinish = () => {
        let searchData = {
            domainId: form.getFieldValue('domainId') || null,
            startDate: form.getFieldValue('dateRange') ? form.getFieldValue('dateRange')[0] : START_DATE,
            endDate: form.getFieldValue('dateRange') ? form.getFieldValue('dateRange')[1] : END_DATE,
        };

        setCurrentPage(1);
        setRequestData({ ...requestData, ...searchData, page: 1 });
    };

    const clearAll = () => {
        setCurrentPage(1);
        setRequestData({
            ...requestData,
            domainId: null,
            startDate: START_DATE,
            endDate: END_DATE,
            page: 1
        });
        form.resetFields();
        setDates([START_DATE, END_DATE]);
    };

    const columns = [
        {
            dataIndex: "timestamp",
            key: "timestamp",
            title: t("domain_monitor.date"),
            width: 80,
            render: (timestamp) => moment(timestamp).format("lll"),
            sorter: true
        },
        {
            dataIndex: "domain",
            key: "domain",
            title: t("domain_monitor.domain"),
            width: 100,
            ellipsis: true,
            sorter: true,
            render: (name, record) => (
                <a href={`/domain/${record.data.domainId}`}>{record.data.domain}</a>
            )
        },
        {
            dataIndex: "event",
            key: "event",
            title: t("domain_monitor.event"),
            width: 100,
            sorter: true,
            render: (event) => event ? event.replace(/_/g, ' ').toUpperCase() : ''
        },
        {
            dataIndex: "description",
            key: "description",
            title: t("domain_monitor.description"),
            width: 100,
            sorter: true
        }
    ];

    const exportHeaders = [
        { label: t("domain_monitor.date"), key: "timestamp" },
        { label: t("domain_monitor.domain"), key: "data.domain" },
        { label: t("domain_monitor.event"), key: "event" },
        { label: t("domain_monitor.description"), key: "description" }
    ];

    /**
     * callback to reflect the changes on dates to the passing request data
     * 
     * @param array values 
     */
    const handleDateChange = createExportDateHandler(requestData);

    return (
        <>
            <PageHeader title={t("domain_monitor.monitoring_alerts_title")} subTitle={t("domain_monitor.monitoring_alerts_title")} />
            <Card>
                <Form
                    form={form}
                    onFinish={onFinish}
                    layout="vertical"
                >
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item
                                name="dateRange"
                                label={t("domain_monitor.date_range")}
                            >
                                <CustomDateRangeV3
                                    t={t}
                                    state={{ dates, setDates }}
                                    unit="months"
                                    limit={MAX_DATERANGE}
                                    setIsDateExceeded={setIsDateExceeded}
                                    onChange={handleDateChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="domainId"
                                label={t("domain_monitor.domain")}
                            >
                                <Select
                                    placeholder={t("domain_monitor.select_domain")}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {domainList.map(domain => (
                                        <Option key={domain.id} value={domain.id}>
                                            {domain.domain}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col> 
                    </Row>
                    <Row gutter={16}>
                        <Col span={24} style={{ textAlign: 'right' }}>
                            <Button onClick={clearAll} style={{ marginRight: 8 }}>
                                {t("domain_monitor.clear_all")}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SearchOutlined />}
                                disabled={isDateExceeded}
                            >
                                {t("domain_monitor.search")}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Card>
            <Card
                style={{ marginTop: 16 }}
                extra={
                    <ExportModalV3
                        t={t}
                        export_header={true}
                        keyname="monitoring_alert_export"
                        title={t("domain_monitor.export_alerts_title")}
                        data_url={DATA_URL_EXPORT}
                        data={{ ...requestData, dm_userids: (() => { try { return JSON.parse(Cookies.get("dm_userids") || "[]"); } catch { return []; } })() }}
                        headers={exportHeaders}
                        filename="monitoring_alerts.csv"
                    />
                }
            >
                <Table
                    columns={columns}
                    dataSource={data.datalist}
                    loading={isLoading}
                    onChange={handleChange}
                    rowKey="id"
                    scroll={{ x: 1100 }}
                    pagination={CustomPaginationV2({
                        totalResult,
                        currentPage,
                        requestData,
                        setRequestData,
                        setCurrentPage
                    })}
                />
            </Card>

            <Modal
                title={modalContent.title}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {modalContent.content}
            </Modal>
        </>
    );
};

export default MonitoringAlerts;
